// React and core libraries
import * as React from "react";
import { useState } from "react";

// Third-party libraries
import { Columns3 } from "lucide-react";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";

// Shadcn components
import { Button } from "../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/ui/command";
import { Switch } from "../../../components/ui/switch";

type ColumnsButtonProps = {
  apiRef: ReturnType<typeof useGridApiRef>;
  columns?: GridColDef[];
  storageKey: string;
  onColumnVisibilityChange: (model: Record<string, boolean>) => void;
  lockNameColumn?: boolean; // Whether to lock the name column as always visible
  initialVisibilityModel?: Record<string, boolean>; // Initial column visibility from DataGrid
};

export const getInitialVisibility = (
  storageKey: string,
  defaultInitial: Record<string, boolean>,
): Record<string, boolean> => {
  const stored = localStorage.getItem(storageKey);
  if (stored) {
    const parsed = JSON.parse(stored) as Record<string, boolean>;
    return parsed;
  }
  return defaultInitial;
};

export const ColumnsButton = React.forwardRef<
  HTMLButtonElement,
  ColumnsButtonProps
>(
  (
    {
      apiRef,
      columns = [],
      storageKey,
      onColumnVisibilityChange,
      lockNameColumn = true,
      initialVisibilityModel,
    },
    ref,
  ): React.ReactElement => {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [localVisibility, setLocalVisibility] = useState<
      Record<string, boolean>
    >(initialVisibilityModel || {});

    const getCurrentVisibility = (): Record<string, boolean> => {
      // Use local state if available, otherwise fall back to localStorage
      if (Object.keys(localVisibility).length > 0) {
        return localVisibility;
      }
      // Last resort: check localStorage
      try {
        const stored = localStorage.getItem(storageKey);
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    };

    const handleColumnToggle = (field: string) => {
      const currentState = getCurrentVisibility();
      const newState = {
        ...currentState,
        [field]: !currentState[field],
      };
      // Update local state immediately for responsive UI
      setLocalVisibility(newState);
      onColumnVisibilityChange(newState);
      // Update the DataGrid's column visibility model
      apiRef.current?.setColumnVisibilityModel(newState);
    };

    const filteredColumns = columns
      .filter((col) => col.field !== "__detail_panel_toggle__")
      .filter((col) =>
        col.headerName?.toLowerCase().includes(searchValue.toLowerCase()),
      );

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-gray-500"
          >
            <Columns3 className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto min-w-56 max-w-80 p-0">
          <Command>
            <CommandInput
              placeholder="Search columns..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList maxHeight="25vh">
              <CommandEmpty>No columns found.</CommandEmpty>
              <CommandGroup>
                {filteredColumns.map((column) => {
                  // TODO don't call getCurrentVisibility for each column
                  const currentVisibility = getCurrentVisibility();
                  const isNameColumn = column.field === "name";

                  // For name column: if locked, always visible; if not locked, use current visibility
                  // For other columns: use current visibility, default to visible if not specified
                  const isVisible = isNameColumn
                    ? lockNameColumn
                      ? true
                      : currentVisibility[column.field] !== false
                    : currentVisibility[column.field] !== false;

                  return (
                    <CommandItem
                      key={column.field}
                      onSelect={() =>
                        !(isNameColumn && lockNameColumn) &&
                        handleColumnToggle(column.field)
                      }
                      className="flex items-center gap-2"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <span
                          className="flex-1 truncate"
                          title={column.headerName}
                        >
                          {column.headerName}
                        </span>
                        <Switch
                          checked={isVisible}
                          disabled={isNameColumn && lockNameColumn}
                          onCheckedChange={() =>
                            !(isNameColumn && lockNameColumn) &&
                            handleColumnToggle(column.field)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
            <div className="flex items-center gap-2 p-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const allHidden = columns.reduce(
                    (acc, col) => {
                      acc[col.field] =
                        col.field === "name" && lockNameColumn ? true : false;
                      return acc;
                    },
                    {} as Record<string, boolean>,
                  );
                  setLocalVisibility(allHidden);
                  onColumnVisibilityChange(allHidden);
                  apiRef.current?.setColumnVisibilityModel(allHidden);
                }}
                className="flex-1"
              >
                Hide All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Show all columns, but respect that some should be hidden by default
                  // This prevents overriding the intended initialState
                  const allVisible = columns.reduce(
                    (acc, col) => {
                      // For now, just show all columns but this could be made smarter
                      // to respect intended default hidden state
                      acc[col.field] = true;
                      return acc;
                    },
                    {} as Record<string, boolean>,
                  );
                  setLocalVisibility(allVisible);
                  onColumnVisibilityChange(allVisible);
                  apiRef.current?.setColumnVisibilityModel(allVisible);
                }}
                className="flex-1"
              >
                Show All
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);
